import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetActiveLeaderboardCycle } from "@/hooks/useGetActiveLeaderboardCycle";
import { DEFAULT_PER_PAGE, useGetActiveLeaderboardRank } from "@/hooks/useGetActiveLeaderboardRank";
import { useGetMyLeaderboardRank } from "@/hooks/useGetMyLeaderboardRank";
import { useHandleError } from "@/hooks/useHandleError";
import { useAuthStore } from "@/store/auth.store";
import type { RankedUser } from "@/types/WebUser";
import LoadingSpinner from "@roman/shared/components/LoadingSpinner";
import { cn } from "@roman/shared/utils";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { LeaderboardInfoCard } from "./components/LeaderboardInfoCard";
import { UserRow } from "./components/UserRow";
import { UserTop3Card } from "./components/UserTop3Card";

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const { users, page, setPage, isLoading } = useGetActiveLeaderboardRank({
    swrOptions: {
      onError: useHandleError("リーダーボードの取得に失敗しました"),
    },
  });

  const { myRank } = useGetMyLeaderboardRank({
    swrOptions: {
      onError: useHandleError("自分の順位の取得に失敗しました"),
    },
  });

  const { leaderboardCycle, isValidating: isLeaderboardCycleLoading } = useGetActiveLeaderboardCycle({
    swrOptions: {
      onError: useHandleError("月間ランキングの取得に失敗しました"),
    },
  });

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });
  const { ref: myRankRef, inView: isMyRankInView } = useInView({ threshold: 1, rootMargin: "0px 0px -70px 0px" }); // -70px is the size of Button at the bottom

  useEffect(() => {
    if (inView) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView, setPage]);

  if (!users.length || isLoading || isLeaderboardCycleLoading) {
    return <LoadingSpinner />;
  }

  const top3Users = users.slice(0, 3);
  const remainingUsers = users.slice(3);
  const isReachedEnd = users.length === 0 || users.length < DEFAULT_PER_PAGE;
  const isShowMyRankInBottom = myRank && myRank.rank > 3 && !isMyRankInView;

  return (
    <div className="min-h-screen bg-white">
      <h1 className="text-xl font-bold py-[11px] px-2">ランキング</h1>

      {leaderboardCycle && <LeaderboardInfoCard leaderboardCycle={leaderboardCycle} currentRank={myRank?.rank} />}

      {/* We'll bring them back in the future */}
      {/* <LeaderboardPeriodTabs selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} /> */}

      <p className="p-2 font-medium">{leaderboardCycle?.name}</p>

      <div className="relative">
        <div className="w-full sticky top-[72px] z-10 grid grid-cols-3 gap-0.5 bg-white p-2 pb-0">
          {top3Users.map((user, index) => (
            <UserTop3Card key={user.id} user={user} index={index} isMyRank={user.id === currentUser?.id} />
          ))}
        </div>
        <div className="min-h-screen">
          {remainingUsers.map((user: RankedUser, index: number) => {
            const isReachedFetchPoint = index + 4 === users.length - Math.floor(users.length * 0.2);

            return (
              <>
                <UserRow
                  key={user.id}
                  user={user}
                  rank={user.rank}
                  className={cn(user.id === currentUser?.id && "bg-blue")}
                  ref={user.id === currentUser?.id ? myRankRef : null}
                />
                {!isLoading && isReachedFetchPoint && !isReachedEnd && <div ref={ref} />}
              </>
            );
          })}

          {(!users.length || isLoading) && page === 1 && (
            <div className="flex flex-col gap-3">
              {Array.from({ length: DEFAULT_PER_PAGE }).map((_, index) => (
                <Skeleton className="w-full h-10" key={index} />
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 w-full bg-white">
          {currentUser && isShowMyRankInBottom && (
            <UserRow user={{ ...currentUser, ...myRank }} rank={myRank.rank} className="bg-blue" />
          )}

          <div className="px-3 py-2.5 relative">
            {myRank && myRank.rank !== 1 && (
              <span className="text-[13px] leading-[13px] text-primary font-bold absolute -top-[14px] left-1/2 -translate-x-1/2 bg-white py-[5px] px-[9px] rounded-full shadow">
                次の順位まで：{Number(myRank.freePointsToNextRank).toLocaleString()} pt
              </span>
            )}

            <Button
              variant="default"
              className="w-full h-[50px] bg-green text-white font-bold rounded-lg text-lg hover:bg-green/90"
              onClick={() => navigate("/redeem-points")}
            >
              ポイントを獲得する
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
